import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, Heart, Mail, UserRound } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    RoomProjectRow,
    SavedRoomDetail,
    Takeoff,
    useSavedRoomsApi,
} from '../../../../common/hooks/use-saved-rooms-api.hook';
import { PicturePlaceholder } from '../../../../components/design-system/common';
import { SidePanel } from '../../../../components/design-system/common/SidePanel';
import { formatDate, formatNumber, RelativeTime, StatusBadge } from '../../../../components/design-system/admin';
import { TakeoffList } from './TakeoffList';

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
    return (
        <section className="flex flex-col gap-2 border-t pt-5 first:border-t-0 first:pt-0">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium">{title}</h3>
                {action}
            </div>
            {children}
        </section>
    );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-end">{children}</span>
        </div>
    );
}

/** The address when the vendor may read it; otherwise whether there is one, kept private. */
const emailLine = (t: any, room: { email: string | null; hasEmail?: boolean }): string =>
    room.email || (room.hasEmail ? t('SAVED_ROOMS.EMAIL_PRIVATE') : t('SAVED_ROOMS.ANONYMOUS'));

const surfaceName = (t: any, surface?: string): string => {
    const type = String(surface || '').split('_')[0];
    return t(`SAVED_ROOMS.SURFACE.${type.toUpperCase()}`, type);
};

/**
 * One saved room: the room as the shopper left it, who they are (and their
 * lead file when the address matches one), what they laid and liked, the
 * quantities, whether the comeback email reaches them, and their video.
 *
 * The pictures and the video only when the shopper shared the room; a
 * private room says so where its picture would be.
 */
export function SavedRoomSheet({ uuid, onClose, onPreview }: { uuid: string | null; onClose: () => void; onPreview: (uuid: string) => void }) {
    const { t } = useTranslation();
    const api = useSavedRoomsApi();
    const [room, setRoom] = useState<SavedRoomDetail | null>(null);

    useEffect(() => {
        if (!uuid) return;
        let alive = true;
        setRoom(null);
        api.room(uuid).then((r) => alive && setRoom(r)).catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [uuid, api]);

    const cb = room?.comeback;
    return (
        <SidePanel open={!!uuid} onOpenChange={(o) => !o && onClose()} title={room?.name || t('SAVED_ROOMS.ROOM')} description={room ? emailLine(t, room) : undefined} width="data-[side=right]:sm:max-w-[640px]">
            {!room ? (
                <Skeleton className="h-[480px] w-full" />
            ) : (
                <div className="flex flex-col gap-5" data-testid="saved-room-sheet">
                    {room.shared ? (
                        <div className="flex flex-col gap-2">
                            <div className="grid grid-cols-3 gap-2">
                                <div className="col-span-2 overflow-hidden rounded-lg border">
                                    {room.renderUrl ? <img src={room.renderUrl} alt="" className="aspect-[4/3] w-full object-cover" /> : <div className="aspect-[4/3] bg-muted" />}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <div className="overflow-hidden rounded-lg border">
                                        {room.roomImageUrl ? <img src={room.roomImageUrl} alt="" className="aspect-[4/3] w-full object-cover" /> : <div className="aspect-[4/3] bg-muted" />}
                                    </div>
                                    <span className="text-xs text-muted-foreground">{t('SAVED_ROOMS.BEFORE')}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant="outline" className="font-normal" data-testid="saved-room-shared-badge">{t('SAVED_ROOMS.SHARED_WITH_YOU')}</Badge>
                                {room.sharedWithVendorAt && <span>{t('SAVED_ROOMS.SHARED_SINCE', { date: formatDate(room.sharedWithVendorAt) })}</span>}
                            </div>
                        </div>
                    ) : (
                        <PicturePlaceholder size="lg" label={t('SAVED_ROOMS.PRIVATE_LONG')} hint={t('SAVED_ROOMS.PRIVATE_HINT')} testId="saved-room-private" />
                    )}

                    <div className="flex flex-wrap gap-2">
                        {room.leadId && (
                            <Button asChild variant="outline" size="sm">
                                <Link to={`/sales/leads/${room.leadId}`} data-testid="saved-room-lead-link">
                                    <UserRound />
                                    {t('SAVED_ROOMS.OPEN_LEAD')}
                                </Link>
                            </Button>
                        )}
                        {room.shareUrl && (
                            <Button asChild variant="outline" size="sm">
                                <a href={room.shareUrl} target="_blank" rel="noreferrer">
                                    <ExternalLink />
                                    {t('SAVED_ROOMS.OPEN_IN_VISUALIZER')}
                                </a>
                            </Button>
                        )}
                    </div>

                    <Section title={t('SAVED_ROOMS.WHO')}>
                        <Fact label={t('SAVED_ROOMS.EMAIL')}>{emailLine(t, room)}</Fact>
                        {room.email && <Fact label={t('SAVED_ROOMS.EMAIL_CONFIRMED')}>{room.emailVerifiedAt ? formatDate(room.emailVerifiedAt) : t('SAVED_ROOMS.NOT_YET')}</Fact>}
                        <Fact label={t('SAVED_ROOMS.SAVED')}>{formatDate(room.createdAt)}</Fact>
                        <Fact label={t('SAVED_ROOMS.LAST_VISIT')}><RelativeTime value={room.lastVisitAt} /></Fact>
                        {room.expiresAt && <Fact label={t('SAVED_ROOMS.KEPT_UNTIL')}>{formatDate(room.expiresAt)}</Fact>}
                        {room.project && <Fact label={t('SAVED_ROOMS.PROJECT')}>{[room.project.name, room.project.clientName].filter(Boolean).join(' · ')}</Fact>}
                    </Section>

                    <Section title={t('SAVED_ROOMS.PRODUCTS_TITLE')}>
                        <div className="divide-y rounded-lg border">
                            {room.products.map((p) => (
                                <div key={`${p.productUuid}:${p.surface || ''}`} className="flex items-center gap-3 p-2.5">
                                    {p.thumbnail ? <img src={p.thumbnail} alt="" className="size-9 rounded-md border object-cover" /> : <span className="size-9 rounded-md border bg-muted" />}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm">{p.name || p.productUuid}</p>
                                        <p className="text-xs text-muted-foreground">{p.surface ? surfaceName(t, p.surface) : t('SAVED_ROOMS.NOT_LAID')}</p>
                                    </div>
                                    {p.liked && (
                                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                            <Heart className="size-3.5" />
                                            {t('SAVED_ROOMS.LIKED')}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Section>

                    <Section title={t('SAVED_ROOMS.TAKEOFF.TITLE')}>
                        <TakeoffList takeoff={room.takeoff} />
                    </Section>

                    <Section
                        title={t('SAVED_ROOMS.COMEBACK.TITLE')}
                        action={
                            <Button variant="outline" size="sm" onClick={() => onPreview(room.uuid)} data-testid="saved-room-preview-comeback">
                                <Mail />
                                {t('SAVED_ROOMS.COMEBACK.PREVIEW')}
                            </Button>
                        }
                    >
                        <Fact label={t('SAVED_ROOMS.COMEBACK.CONSENT')}>
                            {cb?.unsubscribedAt ? (
                                <StatusBadge tone="neutral">{t('SAVED_ROOMS.COMEBACK.UNSUBSCRIBED')}</StatusBadge>
                            ) : cb?.consent ? (
                                <StatusBadge tone="success">{t('SAVED_ROOMS.COMEBACK.YES')}</StatusBadge>
                            ) : (
                                <StatusBadge tone="neutral">{t('SAVED_ROOMS.COMEBACK.NO')}</StatusBadge>
                            )}
                        </Fact>
                        <Fact label={t('SAVED_ROOMS.COMEBACK.SENT')}>{formatNumber(cb?.sentCount ?? 0)}</Fact>
                        {cb?.lastSentAt && <Fact label={t('SAVED_ROOMS.COMEBACK.LAST_SENT')}>{formatDate(cb.lastSentAt)}</Fact>}
                        {cb?.lastError && <p className="text-xs text-muted-foreground">{t('SAVED_ROOMS.COMEBACK.LAST_ERROR', { error: cb.lastError })}</p>}
                    </Section>

                    <Section title={t('SAVED_ROOMS.VIDEO.TITLE')}>
                        {room.video.status === 'ready' && room.video.url ? (
                            <video src={room.video.url} controls muted playsInline className="w-full rounded-lg border bg-black" data-testid="saved-room-video" />
                        ) : room.video.status === 'ready' && !room.shared ? (
                            <p className="text-sm text-muted-foreground">{t('SAVED_ROOMS.VIDEO.PRIVATE')}</p>
                        ) : (
                            <p className="text-sm text-muted-foreground">{t(`SAVED_ROOMS.VIDEO.STATUS.${room.video.status.toUpperCase()}`)}</p>
                        )}
                    </Section>
                </div>
            )}
        </SidePanel>
    );
}

/** A professional's project: the client, the rooms, the quantities and the board. */
export function ProjectSheet({ project, onClose }: { project: RoomProjectRow | null; onClose: () => void }) {
    const { t } = useTranslation();
    const api = useSavedRoomsApi();
    const [takeoff, setTakeoff] = useState<Takeoff | null>(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!project) return;
        let alive = true;
        setTakeoff(null);
        api.takeoff(project.uuid).then((r) => alive && setTakeoff(r)).catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [project, api]);

    const download = async () => {
        if (!project) return;
        setDownloading(true);
        try {
            const buf = await api.boardPdf(project.uuid);
            const url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${project.name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'board'}.pdf`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <SidePanel open={!!project} onOpenChange={(o) => !o && onClose()} title={project?.name || ''} description={project?.clientName ? t('SAVED_ROOMS.FOR_CLIENT', { client: project.clientName }) : undefined} width="data-[side=right]:sm:max-w-[640px]">
            {project && (
                <div className="flex flex-col gap-5" data-testid="project-sheet">
                    <div className="grid grid-cols-3 gap-2">
                        {project.thumbnails.map((src) => (
                            <img key={src} src={src} alt="" className="aspect-[4/3] w-full rounded-lg border object-cover" />
                        ))}
                        {/* The rooms the shopper kept to themselves, as neutral frames. */}
                        {Array.from({ length: Math.max(0, Math.min(3, project.rooms.length) - project.thumbnails.length) }, (_, i) => (
                            <PicturePlaceholder key={`private-${i}`} size="md" label={t('SAVED_ROOMS.PRIVATE')} className="aspect-[4/3]" />
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={download} disabled={downloading} data-testid="project-download-board">
                            <Download />
                            {t('SAVED_ROOMS.DOWNLOAD_BOARD')}
                        </Button>
                        {project.leadId && (
                            <Button asChild variant="outline">
                                <Link to={`/sales/leads/${project.leadId}`}>
                                    <UserRound />
                                    {t('SAVED_ROOMS.OPEN_LEAD')}
                                </Link>
                            </Button>
                        )}
                    </div>
                    <Section title={t('SAVED_ROOMS.WHO')}>
                        <Fact label={t('SAVED_ROOMS.EMAIL')}>{project.email || (project.hasEmail ? t('SAVED_ROOMS.EMAIL_PRIVATE') : t('SAVED_ROOMS.ANONYMOUS'))}</Fact>
                        <Fact label={t('SAVED_ROOMS.ROOMS_COUNT_LABEL')}>{formatNumber(project.rooms.length)}</Fact>
                        <Fact label={t('SAVED_ROOMS.LAST_VISIT')}><RelativeTime value={project.lastVisitAt} /></Fact>
                    </Section>
                    <Section title={t('SAVED_ROOMS.TAKEOFF.TITLE')}>{takeoff ? <TakeoffList takeoff={takeoff} testId="project-takeoff" /> : <Skeleton className="h-40 w-full" />}</Section>
                    {project.notes && (
                        <Section title={t('SAVED_ROOMS.NOTES')}>
                            <p className="text-sm">{project.notes}</p>
                        </Section>
                    )}
                </div>
            )}
        </SidePanel>
    );
}
